import lg_brixo from "@/assets/lenders/brixo.webp";
import lg_credifi from "@/assets/lenders/credifi.webp";
import lg_creditstar from "@/assets/lenders/creditstar.webp";
import lg_fairlo from "@/assets/lenders/fairlo.webp";
import lg_ferratum from "@/assets/lenders/ferratum.webp";
import lg_loansse from "@/assets/lenders/loansse.webp";
import lg_northmill from "@/assets/lenders/northmill.webp";
import lg_saldo from "@/assets/lenders/saldo.webp";
import lg_tomly from "@/assets/lenders/tomly.webp";
import lg_anyfin from "@/assets/lenders/anyfin.webp";
import lg_banky from "@/assets/lenders/banky.webp";
import lg_flexkontot from "@/assets/lenders/flexkontot.webp";
import lg_loanstep from "@/assets/lenders/loanstep.webp";
import lg_merax from "@/assets/lenders/merax.webp";
import lg_plus1 from "@/assets/lenders/plus1.webp";
import lg_thorn from "@/assets/lenders/thorn.webp";
import lg_finlo from "@/assets/lenders/finlo.webp";
import lg_ekonomen from "@/assets/lenders/ekonomen.webp";
import lg_moneezy from "@/assets/lenders/moneezy.webp";
import lg_enkelfinans from "@/assets/lenders/enkelfinans.webp";
import advisa from "@/assets/lenders/advisa.webp";
import axo from "@/assets/lenders/axo.webp";
import enklare from "@/assets/lenders/enklare.webp";
import klaralan from "@/assets/lenders/klaralan.webp";
import lendo from "@/assets/lenders/lendo.webp";
import lumify from "@/assets/lenders/lumify.webp";
import sambla from "@/assets/lenders/sambla.webp";

export type LenderCategory =
  | "Privatlån"
  | "Billån"
  | "Samla lån"
  | "Snabblån"
  | "SMS-lån"
  | "Lån utan UC"
  | "Kreditkort"
  | "Renovering"
  | "Resa"
  | "Övrigt";

export type Lender = {
  id: string;
  slug: string;
  name: string;
  initials: string;
  color: string;
  logoUrl?: string;
  minAmount: number;
  maxAmount: number;
  termLabel: string;
  term: string;
  rate: string;
  setupFee: string;
  adminFee: string;
  invoiceFee: string;
  usesUc: boolean;
  directPayout: boolean;
  allowsRemarks: boolean;
  minAge: number;
  minIncome?: number;
  requiresFixedEmployment?: boolean;
  rating: number;
  reviews: number;
  categories: LenderCategory[];
  badge?: string;
  example: string;
  description: string;
  tags?: string[];
  extraLink?: { href: string; label: string; description?: string };
  descriptionHighlight?: string;
  // legacy field kept for older components:
  effRate?: string;
};

const P = (o: Lender): Lender => o;

const ALL_LENDERS: Record<string, Lender> = {
  lendo: P({
    id: "lendo", slug: "lendo", name: "Lendo", initials: "LE",
    color: "from-emerald-500 to-emerald-700", logoUrl: lendo,
    minAmount: 10000, maxAmount: 800000,
    termLabel: "1 - 15 år", term: "1 – 15 år",
    rate: "4,95% - 23,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 18, minIncome: 10000,
    rating: 4.8, reviews: 21480,
    categories: ["Privatlån", "Billån", "Samla lån"],
    example: "Annuitetslån 12 år. Effektiv årsränta 9,63%. Ett lån på 200 000 kr kostar då 2 302 kr/månad (144 avbetalningar), dvs totalt 331 495 kr. Ingen start-/aviavgift. 9,23% nominell ränta (rörlig ränta, sätts individuellt baserat på dina förutsättningar). Ansökan kommer att skickas till de kreditgivare som bäst matchar din profil, uppdaterat 2025-01-09.",
    description: "Sveriges största låneförmedlare. En ansökan – bud från 40+ banker. Endast en UC oavsett antal bud.",
    tags: ["Jämför 40+ banker", "1 UC"],
  }),
  enklare: P({
    id: "enklare", slug: "enklare", name: "Enklare", initials: "EN",
    color: "from-slate-600 to-slate-800", logoUrl: enklare,
    minAmount: 5000, maxAmount: 800000,
    termLabel: "1 - 20 år", term: "1 – 20 år",
    rate: "4,50% - 22,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "20 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 18, minIncome: 10000,
    rating: 4.6, reviews: 5210,
    categories: ["Privatlån", "Samla lån", "Renovering", "Resa", "Övrigt"],
    example: "Annuitetslån 12 år, belopp 400 000 kr, rörlig ränta 7,99%, uppläggningskostnad 400 kr, aviavgift 20 kr, ger en effektiv ränta på 8,41%. Totalt belopp att återbetala 626 457 kr, fördelat på 144 återbetalningar, ger en månadskostnad på 4 348 kr. Återbetalningstid 1-20 år. Maximala räntan är 23,00%. Räntespann mellan: 4,95% - 23,00%. Uppdaterat 2025-03-01",
    description: "Snabb ansökan online – personliga bud från 30+ svenska banker med endast en UC.",
    tags: ["30+ banker", "1 UC"],
  }),
  sambla: P({
    id: "sambla", slug: "sambla", name: "Sambla", initials: "SA",
    color: "from-red-500 to-rose-700", logoUrl: sambla,
    minAmount: 5000, maxAmount: 800000,
    termLabel: "1 - 20 år", term: "1 – 20 år",
    rate: "4,50% - 21,99%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 18, minIncome: 8400,
    rating: 4.7, reviews: 12180,
    categories: ["Privatlån", "Billån", "Samla lån"],
    example: "Räkneexempel: Annuitetslån på 350 000 kr, 12 års löptid, nominell ränta 8,19% och 0 kr i uppläggnings-/aviavgift blir den effektiva räntan 8,50%. Total lånekostnad: 550 820 kr. Månadskostnad: 3 825 kr fördelat på 144 betalningstillfällen. Din ansökan kommer att skickas till de kreditgivare som bäst matchar din profil. Återbetalningstid 1-20 år. Räntespann mellan: 4,50% - 21,99%. (Uppdaterat augusti 2025).",
    description: "Har hjälpt över 500 000 svenskar sänka sin lånekostnad. Endast en UC oavsett antal bud.",
    tags: ["40+ banker", "1 UC"],
  }),
  advisa: P({
    id: "advisa", slug: "advisa", name: "Advisa", initials: "AD",
    color: "from-cyan-400 to-cyan-600", logoUrl: advisa,
    minAmount: 5000, maxAmount: 800000,
    termLabel: "1 - 20 år", term: "1 – 20 år",
    rate: "4,50% - 21,99%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 18, minIncome: 8800,
    rating: 4.7, reviews: 4850,
    categories: ["Privatlån", "Samla lån"],
    example: "Räkneexempel: Annuitetslån på 350 000 kr, 12 års löptid, nominell ränta 8,19% och 0 kr i uppläggnings-/aviavgift blir den effektiva räntan 8,50%. Total lånekostnad: 550 820 kr. Månadskostnad: 3 825 kr fördelat på 144 betalningstillfällen. Din ansökan kommer att skickas till de kreditgivare som bäst matchar din profil. Återbetalningstid 1-20 år. Räntespann mellan: 4,50% - 21,99%. (Uppdaterat augusti 2025).",
    description: "En av Sveriges mest etablerade låneförmedlare – fri, personlig rådgivning ingår.",
    tags: ["30+ banker", "1 UC"],
  }),
  axo: P({
    id: "axo", slug: "axo", name: "Axo Finans", initials: "AX",
    color: "from-amber-400 to-amber-600", logoUrl: axo,
    minAmount: 5000, maxAmount: 800000,
    termLabel: "1 - 15 år", term: "1 – 15 år",
    rate: "4,95% - 22,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 18, minIncome: 8800,
    rating: 4.5, reviews: 2100,
    categories: ["Privatlån", "Samla lån", "Billån"],
    example: "Räkneexempel: Annuitetslån 185 000 kr på 12 år (144 avbetalningar). Nom.ränta: 6,95 %. Eff. ränta: 7,18 %. Totalt att betala 273 251 kr. Månadskostnad: 1 898 kr. Ingen start-/aviavgift. Uppdaterad september 2025. Återbetalningstid 1–15 år. Nominell ränta: 4,95–22,00 %. Rörlig ränta, sätts individuellt baserat på dina förutsättningar.",
    description: "Nordisk låneförmedlare som även accepterar tidigare betalningsanmärkningar.",
    tags: ["Anmärkning OK", "1 UC"],
  }),
  loansse: P({
    id: "loansse", slug: "loans-se", name: "Loans.se", initials: "LS",
    color: "from-blue-500 to-blue-700", logoUrl: lg_loansse,
    minAmount: 5000, maxAmount: 800000,
    termLabel: "1 - 20 år", term: "1 – 20 år",
    rate: "4,50% - 22,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "20 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 18,
    rating: 4.3, reviews: 980,
    categories: ["Privatlån", "Billån"],
    example: "Räkneexempel: Annuitetslån 12 år, belopp 400 000 kr, rörlig ränta 7,99 %, uppläggningsavgift 400 kr, aviavgift 20 kr, ger en effektiv ränta på 8,41 %. Totalt belopp att återbetala 626 457 kr, fördelat på 144 återbetalningar, ger en månadskostnad på 4 348 kr. Återbetalningstid 1–20 år. Maximal ränta är 22,00 %. Räntespann: 4,50–22,00 %. Uppdaterat 2025-08-15.",
    description: "Låneförmedlare med fokus på privatlån och billån – bud från flera banker med en UC.",
    tags: ["Privatlån & billån", "1 UC"],
  }),
  klaralan: P({
    id: "klaralan", slug: "klara-lan", name: "Klara Lån", initials: "KL",
    color: "from-lime-500 to-green-700", logoUrl: klaralan,
    minAmount: 5000, maxAmount: 150000,
    termLabel: "1 - 8 år", term: "1 – 8 år",
    rate: "7,90% - 23,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "39 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 18,
    rating: 4.2, reviews: 1120,
    categories: ["Snabblån", "SMS-lån", "Lån utan UC"],
    example: "Räkneexempel: Räntan är rörlig och sätts individuellt. Ett lån på 30 000 kr till 23 procents nominell ränta med en återbetalningstid på 24 månader, med 24 avbetalningar om 1 610 kronor och 350 kronor i uppläggningsavgift samt 39 kronor i aviavgift, ger en effektiv ränta på 30,38 procent. Totalt att återbetala blir 38 995 kronor, uppdaterat 2025-02-27. Återbetalningstid 1-8 år. Effektiv räntespann mellan: 18,89%-612,29%.",
    description: "Snabb kreditgivare utan UC. Beslut direkt online och pengarna samma dag.",
    tags: ["Utan UC", "Direkt"],
  }),
  fairlo: P({
    id: "fairlo", slug: "fairlo", name: "Fairlo", initials: "FA",
    color: "from-pink-500 to-fuchsia-700", logoUrl: lg_fairlo,
    minAmount: 10000, maxAmount: 70000,
    termLabel: "3 - 72 mån", term: "3 – 72 månader",
    rate: "20,00% - 22,00%", setupFee: "0 kr", adminFee: "59 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: false,
    minAge: 20,
    rating: 4.6, reviews: 3100,
    categories: ["Lån utan UC", "Snabblån"],
    example: "Räkneexempel vid ett låneutrymme på 40 000 kr, en nominell rörlig ränta på 22 % och en återbetalningstid på 72 månader uppgår månadsbeloppet till 1 064 kr. Den effektiva räntan är 28,18 % och det totala beloppet att återbetala är 77 199 kr. I månadsbeloppet ingår en månadsavgift om 59 kr. Därutöver tillkommer en uppläggningsavgift om 588 kr som betalas separat i samband med att krediten tas i bruk.",
    description: "Digital kontokredit utan UC (använder Bisnode). Flexibel återbetalning.",
    tags: ["Utan UC", "Flexibel"],
  }),
  northmill: P({
    id: "northmill", slug: "northmill-kontokredit", name: "Northmill Kontokredit", initials: "NM",
    color: "from-orange-500 to-red-600", logoUrl: lg_northmill,
    minAmount: 1000, maxAmount: 50000,
    termLabel: "12 - 60 mån", term: "12 – 60 månader",
    rate: "7,90% - 21,90%", setupFee: "245 kr", adminFee: "29 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 18,
    rating: 4.8, reviews: 8450,
    categories: ["Lån utan UC", "SMS-lån", "Kreditkort"],
    example: "Individuell räntesättning 7,90 % - 21,90 % (Effektiv ränta 9,71 % - 29,34 %). Prisexempel: Vid kredit om 50 000 kr till 19,50 % årsränta (rörlig) under fem år med 60 återbetalningar, en uttagsavgift om 245 kr samt en administrativ månadsavgift om 29 kr blir månadsbeloppet 1 347 kr (totalt 80 775 kr) och den effektiva räntan 22,87 %.",
    description: "Digital svensk bank. Kontokredit utan UC upp till 50 000 kr, pengarna på minuter.",
    tags: ["Utan UC", "Digital bank"],
  }),
  brixokk: P({
    id: "brixokk", slug: "brixo-kontokredit", name: "Brixo Kontokredit", initials: "BR",
    color: "from-teal-500 to-teal-700", logoUrl: lg_brixo,
    minAmount: 5000, maxAmount: 50000,
    termLabel: "6 - 48 mån", term: "6 – 48 månader",
    rate: "21,95%", setupFee: "0 kr", adminFee: "25 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 21,
    rating: 4.5, reviews: 2400,
    categories: ["Lån utan UC", "SMS-lån", "Snabblån"],
    example: "Krediten har 19,95 % nominell rörlig årsränta, 475 kr i uppläggningsavgift och 25 kr månatlig administrationsavgift. En exempelkredit på 50 000 kr som återbetalas med 1 599 kr per månad under 48 månader har en effektiv årsränta på 25,42 %. Det innebär en total kostnad för krediten om 26 749 kr. Totalt belopp att återbetala är 76 749 kr. Löptid från 6 till 48 månader.",
    description: "Flexibel kontokredit utan UC (Bisnode). Snabb kreditbedömning digitalt.",
    tags: ["Utan UC", "Flexibel"],
  }),
  brixopl: P({
    id: "brixopl", slug: "brixo-privatlan", name: "Brixo Privatlån", initials: "BP",
    color: "from-teal-600 to-emerald-800", logoUrl: lg_brixo,
    minAmount: 10000, maxAmount: 200000,
    termLabel: "3 - 6 år", term: "3 – 6 år",
    rate: "19,95%", setupFee: "475 kr", adminFee: "25 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 21,
    rating: 4.4, reviews: 1850,
    categories: ["Privatlån", "Lån utan UC"],
    example: "Krediten har 19,95 % nominell rörlig årsränta, 475 kr i uppläggningsavgift och 25 kr månatlig administrationsavgift. En exempelkredit på 50 000 kr som återbetalas med 1 599 kr per månad under 48 månader har en effektiv årsränta på 25,42 %. Det innebär en total kostnad för krediten om 26 749 kr. Totalt belopp att återbetala är 76 749 kr. Löptid från 6 till 48 månader.",
    description: "Privatlån utan UC-upplysning. Fast ränta och tydliga villkor via Bisnode-kontroll.",
    tags: ["Utan UC", "Privatlån"],
  }),
  credifi: P({
    id: "credifi", slug: "credifi", name: "Credifi", initials: "CF",
    color: "from-violet-500 to-purple-700", logoUrl: lg_credifi,
    minAmount: 2000, maxAmount: 200000,
    termLabel: "3 - 92 mån", term: "3 – 92 månader",
    rate: "22,00%", setupFee: "588 kr", adminFee: "0 kr", invoiceFee: "57 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 18,
    rating: 4.3, reviews: 1680,
    categories: ["Snabblån", "SMS-lån", "Lån utan UC"],
    example: "En kredit på 20 000 kr till 22 % ränta med en återbetalningstid på sjutton månader (med sjutton återbetalningar om 1 472 kr vardera) och 588 kr i uppläggningsavgift samt 969 kr i aviavgifter ger en effektiv ränta på totalt 36,21 %. Det totala beloppet att återbetala är 25 017 kr. Kreditens löptid och där tillhörande kostnader kan komma att förändras vid utökning av krediten.",
    description: "Digital snabbkredit utan UC (Creditsafe). Utbetalning inom minuter.",
    tags: ["Utan UC", "Direkt"],
  }),
  saldo: P({
    id: "saldo", slug: "saldo-bank", name: "Saldo Bank", initials: "SB",
    color: "from-sky-500 to-blue-700", logoUrl: lg_saldo,
    minAmount: 1000, maxAmount: 30000,
    termLabel: "3 - 96 mån", term: "3 – 96 månader",
    rate: "23,00%", setupFee: "588 kr", adminFee: "39 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: false,
    minAge: 18,
    rating: 4.2, reviews: 1410,
    categories: ["Snabblån", "SMS-lån", "Lån utan UC"],
    badge: "DIREKT UTBETALNING",
    example: "En kontokredit med en kreditgräns upp till 40 000 kr med en bunden marginalränta på 20 %, en referensränta på 2 % som fastställs halvårsvis (tot. 22 %), en effektiv årsränta på 30,04–78,04 %, och en återbetalningsperiod på 3–96 månader, beroende på det lyfta lånebeloppet. Uppläggningsavgift 390 kr, månadsavgift 39 kr per månad och uttagsavgift 195 kr.",
    description: "Snabblån utan UC med direktutbetalning dygnet runt via Swish/Bank.",
    tags: ["Utan UC", "Direkt"],
  }),
  creditstar: P({
    id: "creditstar", slug: "creditstar", name: "CreditStar", initials: "CS",
    color: "from-yellow-500 to-orange-600", logoUrl: lg_creditstar,
    minAmount: 5000, maxAmount: 150000,
    termLabel: "1 - 7 år", term: "1 – 7 år",
    rate: "20,00% fast", setupFee: "300 kr", adminFee: "30 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 21,
    rating: 4.1, reviews: 920,
    categories: ["Snabblån", "SMS-lån", "Lån utan UC"],
    badge: "FAST RÄNTA",
    example: "Ett lån på 30 000 kr med 20 % nominell fast ränta, 300 kr i uppläggningsavgift (betalas med den första månadsbetalningen) och en månadsavgift på 30 kr, med en återbetalningstid på 60 månader, ger en effektiv ränta på 25,06 %. Det totala beloppet att återbetala blir 49 788,84 kr, fördelat på 60 månadsbetalningar: den första på 1 124,82 kr och därefter 59 delbetalningar på 824,82 kr vardera.",
    description: "Lån utan UC med fast ränta genom hela löptiden. Digital ansökan via BankID.",
    tags: ["Utan UC", "Fast ränta"],
  }),
  tomly: P({
    id: "tomly", slug: "tomly", name: "Tomly", initials: "TM",
    color: "from-rose-400 to-pink-600", logoUrl: lg_tomly,
    minAmount: 2000, maxAmount: 70000,
    termLabel: "4 - 19 mån", term: "4 – 19 månader",
    rate: "23,00%", setupFee: "588 kr", adminFee: "0 kr", invoiceFee: "57 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 20,
    rating: 4.0, reviews: 650,
    categories: ["Snabblån", "SMS-lån", "Lån utan UC"],
    badge: "KORT LÖPTID",
    example: "En kredit på 20 000 kr till 22,00 % ränta med en återbetalningstid på sjutton månader (med sjutton återbetalningar om 1 472 kr vardera) och 588 kr i uppläggningsavgift samt 969 kr i aviavgifter ger en effektiv ränta på totalt 36,21 %. Det totala beloppet att återbetala är 25 017 kr. Kreditens löptid och där tillhörande kostnader kan komma att förändras vid utökning av krediten.",
    description: "Kortfristig kredit utan UC för akuta utgifter. Ingen bindningstid.",
    tags: ["Utan UC", "Kort löptid"],
  }),
  ferratum: P({
    id: "ferratum", slug: "ferratum", name: "Ferratum", initials: "FE",
    color: "from-red-500 to-red-700", logoUrl: lg_ferratum,
    minAmount: 1000, maxAmount: 45000,
    termLabel: "4 - 46 mån", term: "4 – 46 månader",
    rate: "23,00%", setupFee: "0 kr", adminFee: "59 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 21,
    rating: 4.4, reviews: 4200,
    categories: ["Snabblån", "SMS-lån", "Lån utan UC"],
    example: "Om ett lån på 23 000 SEK tas ut och återbetalas i 12 månatliga delbetalningar om [1. 2 962,33 SEK, 2. 2 362,19 SEK, 3. 2 327,06 SEK, 4. 2 291,92 SEK, 5. 2 256,78 SEK, 6. 2 221,64 SEK, 7. 2 186,50 SEK, 8. 2 151,36 SEK, 9. 2 116,22 SEK, 10. 2 081,08 SEK, 11. 2 045,94 SEK, 12. 2 010,81 SEK], är den effektiva räntan 37,68 % och den rörliga nominella årsräntan är 21,9999735 %. Lånet har en uppläggningsavgift på 565 kr och en månatlig kontoavgift på 59 kr. Totala beloppet att återbetala är 27 013,83 SEK. Löptiden för kreditavtalet är obestämd. Detta exempel baseras på antagandet att lånet återbetalas i 12 lika stora kapitaldelbetalningar. Exemplet baseras på antagandet att lånebeloppet tas ut vid ett tillfälle och återbetalas i tid. Låna ansvarsfullt genom att utvärdera återbetalningsalternativen! Tjänsten erbjuds av Multitude Bank plc, Adress: ST Business Center 120, The Strand Gzira, GZR 1027, Malta. Registreringsnummer: C-56251.",
    description: "Etablerad europeisk mikrokreditgivare. Snabb kreditbedömning via CRIF/Creditsafe.",
    tags: ["Utan UC", "Snabb"],
  }),
  lumify: P({
    id: "lumify", slug: "lumify", name: "Lumify", initials: "LU",
    color: "from-yellow-400 to-amber-500", logoUrl: lumify,
    minAmount: 2000, maxAmount: 200000,
    termLabel: "3 - 92 mån", term: "3 – 92 månader",
    rate: "22,00%", setupFee: "588 kr", adminFee: "0 kr", invoiceFee: "57 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 18,
    rating: 4.2, reviews: 2100,
    categories: ["Snabblån", "SMS-lån", "Lån utan UC"],
    badge: "DIREKT UTBETALNING",
    example: "En kredit på 20 000 kr till 22 % ränta med en återbetalningstid på femton månader (med femton återbetalningar om 2 197 kr, 1 880 kr, 1 854 kr, 1 829 kr, 1 803 kr, 1 777 kr, 1 752 kr, 1 726 kr, 1 700 kr, 1 674 kr, 1 649 kr, 1 623 kr, 1 597 kr, 1 572 kr samt 1 546 kr) och 588 kr i uppläggningsavgift, 1 675 kr i serviceavgift för avbetalningsplan samt 855 kr i aviavgifter ger en effektiv ränta på totalt 55,65 %. Det totala beloppet att återbetala är 26 178 kr. Kreditens löptid och där tillhörande kostnader kan komma att förändras vid utökning av krediten.",
    description: "Modern konsumentkredit utan UC. Utbetalning inom 15 minuter, dygnet runt.",
    tags: ["Utan UC", "Direkt"],
  }),
  flexkonto: P({
    id: "flexkonto", slug: "flexkontot", name: "Flexkontot", initials: "FK",
    color: "from-sky-500 to-blue-700", logoUrl: lg_flexkontot,
    minAmount: 3000, maxAmount: 40000,
    termLabel: "3 - 96 mån", term: "3 – 96 månader",
    rate: "22,98%", setupFee: "457 kr", adminFee: "39 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 21,
    rating: 4.3, reviews: 1100,
    categories: ["Lån utan UC", "SMS-lån", "Snabblån"],
    badge: "SNABBA BESKED",
    example: "Krediten har 21,95 % nominell rörlig årsränta (löptid 3–96 månader), 575 kr i uppläggningsavgift och 39 kr månatlig administrationsavgift. En exempelkredit på 20 000 kr som återbetalas med 1 964 kr per månad under 12 månader har en effektiv årsränta på 36,4 %. Det innebär en total kostnad för krediten om 3 568 kr. Totala beloppet att återbetala är 23 568 kr. Löptiden för kreditavtalet är obestämd.",
    description: "Löpande kontokredit utan UC. Betala i din takt utan bindningstid.",
    tags: ["Utan UC", "Flexibel"],
  }),
  merax: P({
    id: "merax", slug: "merax", name: "Merax", initials: "MX",
    color: "from-indigo-500 to-blue-700", logoUrl: lg_merax,
    minAmount: 3000, maxAmount: 40000,
    termLabel: "9 - 72 mån", term: "9 – 72 månader",
    rate: "21,95%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 21,
    rating: 4.1, reviews: 890,
    categories: ["Lån utan UC", "SMS-lån", "Snabblån"],
    example: "Krediten har 21,95 % nominell rörlig årsränta (löptid 9–72 månader), 0 kr i uppläggningsavgift och 0 kr i aviavgift. En exempelkredit på 20 000 kr har en effektiv årsränta på 24,3 %. Inga uppläggningsavgifter eller aviavgifter tillkommer. Löptiden för kreditavtalet är obestämd. Uppdaterat september 2026.",
    effRate: "24,3%",
    description: "Mindre lån utan UC. BankID-signering och pengarna direkt utan avgifter.",
    tags: ["Utan UC", "0 kr avgift"],
  }),
  plus1stora: P({
    id: "plus1stora", slug: "plus1-stora-samlingslanet", name: "Plus 1 – Stora Samlingslånet", initials: "P1",
    color: "from-emerald-600 to-emerald-800", logoUrl: lg_plus1,
    minAmount: 10000, maxAmount: 500000,
    termLabel: "5 - 12 år", term: "5 – 12 år",
    rate: "5,00% - 22,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 21, minIncome: 17000,
    rating: 4.7, reviews: 1350,
    categories: ["Samla lån", "Privatlån"],
    badge: "NYHET! RÄNTAN SÄNKS VAR 3e MÅNAD",
    example: "Räkneexempel samlingslånet: Räntan är rörlig och sätts individuellt. För ett annuitetslån på 160 000 kr där räntan startar på 22,00 % och sänks var tredje månad och en återbetalningstid på 8 år med 96 st avbetalningar om i snitt 2 952 kr och 588 kr i uppläggningsavgift ger en effektiv ränta på totalt 18,89 %. Totalt att återbetala blir 283 924 kr.",
    description: "Specialist på större samlingslån. Räntan sänks automatiskt var 3:e månad.",
    descriptionHighlight: "Accepterar även skulder hos Kronofogden.",
    tags: ["Samla lån", "KFM OK"],
  }),
  plus1lilla: P({
    id: "plus1lilla", slug: "plus1-lilla-samlingslanet", name: "Plus 1 – Lilla Samlingslånet", initials: "P1",
    color: "from-emerald-500 to-teal-700", logoUrl: lg_plus1,
    minAmount: 10000, maxAmount: 50000,
    termLabel: "1 - 5 år", term: "1 – 5 år",
    rate: "13,00% - 22,50%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 21, minIncome: 22000,
    rating: 4.5, reviews: 920,
    categories: ["Samla lån", "Privatlån"],
    badge: "NYHET! RÄNTAN SÄNKS VAR 3e MÅNAD",
    example: "Räntan är rörlig och sätts individuellt. För ett annuitetslån på 30 000 kr där räntan startar på 22,50 % och sänks med 0,5 procentenheter var tredje månad och en återbetalningstid på 5 år med 60 st avbetalningar om i snitt 782 kr och 588 kr i uppläggningsavgift ger en effektiv ränta på totalt 22,72 %. Totalt att återbetala blir 47 614 kr.",
    description: "Mindre samlingslån med sjunkande ränta över tid. Perfekt för att samla dyra krediter.",
    tags: ["Samla lån", "Sjunkande ränta"],
  }),
  northmillsamla: P({
    id: "northmillsamla", slug: "northmill-samlingslan", name: "Northmill Samlingslån", initials: "NS",
    color: "from-orange-600 to-red-700", logoUrl: lg_northmill,
    minAmount: 10000, maxAmount: 300000,
    termLabel: "1 - 15 år", term: "1 – 15 år",
    rate: "Individuell (lägre än din nuvarande)", setupFee: "495 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: false,
    minAge: 18,
    rating: 4.8, reviews: 5120,
    categories: ["Samla lån", "Privatlån"],
    badge: "GARANTERAT LÄGRE RÄNTA",
    extraLink: {
      href: "https://www.northmill.com/se/garantivillkor/",
      label: "Läs garantivillkor",
      description: "Individuell ränta, garanterat lägre än din nuvarande. Garantin gäller under de villkor och förutsättningar som anges här.",
    },
    example: "Prisexempel: Vid lån om 100 000 kr till 15,37 % årsränta (rörlig) under 7 år med 84 återbetalningar och en uppläggningsavgift om 495 kr blir totalt belopp att återbetala 164 336 kr (1 950 kr per månad) och den effektiva räntan 16,70 %.",
    description: "Northmill garanterar en lägre ränta än ditt nuvarande snittlåneutrymme — annars får du mellanskillnaden.",
    tags: ["Garanti", "Samla lån"],
  }),
  anyfin: P({
    id: "anyfin", slug: "anyfin", name: "Anyfin", initials: "AF",
    color: "from-purple-500 to-purple-700", logoUrl: lg_anyfin,
    minAmount: 1000, maxAmount: 500000,
    termLabel: "3 - 120 mån", term: "3 – 120 månader",
    rate: "från 5,35%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: false,
    minAge: 18,
    rating: 4.8, reviews: 15400,
    categories: ["Samla lån", "Kreditkort"],
    example: "Ett refinansieringslån på 43 000 kronor till 13,65 % nominell ränta (rörlig) med en återbetalningstid på 60 månader, med 60 avbetalningar om 993 kronor ger en effektiv ränta på totalt 14,54 %. Totalt att återbetala blir 59 565 kronor.",
    description: "Sänker räntan på dina befintliga lån och kreditkort. Skicka en bild på fakturan – Anyfin gör resten.",
    tags: ["Refinansiering", "Kreditkort"],
  }),
  banky: P({
    id: "banky", slug: "banky", name: "Banky", initials: "BK",
    color: "from-blue-600 to-indigo-800", logoUrl: lg_banky,
    minAmount: 10000, maxAmount: 150000,
    termLabel: "2 - 12 år", term: "2 – 12 år",
    rate: "22,95% fast", setupFee: "199 kr", adminFee: "0 kr", invoiceFee: "19 kr",
    usesUc: false, directPayout: false, allowsRemarks: true,
    minAge: 18,
    rating: 4.6, reviews: 1890,
    categories: ["Privatlån", "Snabblån"],
    badge: "FAST RÄNTA",
    example: "Räkneexempel: *Annuitetslån 5 år, effektiv årsränta 26,5 %. Ett lån på 60 000 kr kostar 1 709 kr/mån (60 avbetalningar), totalt 42 821 kr inkl. 199 kr i uppläggningsavgift och 19 kr i aviavgifter. 22,95 % nominell ränta. Rörlig ränta. Banky samarbetar med Nordiska Kreditmarknadsaktiebolaget. Uppdaterat 2025-02-28.",
    description: "Privatlån med fast ränta. Accepterar tidigare anmärkningar och KFM-skuld.",
    tags: ["Fast ränta", "KFM OK"],
  }),
  thorn: P({
    id: "thorn", slug: "thorn", name: "Thorn SE", initials: "TH",
    color: "from-gray-600 to-gray-800", logoUrl: lg_thorn,
    minAmount: 10000, maxAmount: 150000,
    termLabel: "1 - 7 år", term: "1 – 7 år",
    rate: "14,88% - 21,6%", setupFee: "495 kr", adminFee: "19 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: false,
    minAge: 20,
    rating: 4.2, reviews: 2150,
    categories: ["Privatlån"],
    example: "Representativt exempel: Ett lån på 45 000 kronor till 21,60 % rörlig ränta med en återbetalningstid på 82 månader, med 82 avbetalningar om 1 066 kronor och 495 kronor i uppläggningsavgift (som läggs till lånet) samt 19 kronor i administrationsavgift, ger en effektiv ränta på totalt 25,24 %. Totalt att återbetala blir 88 945 kronor.",
    description: "Etablerad kreditgivare med attraktiv fast ränta för kunder med god kredithistorik.",
    tags: ["Lägre ränta", "Privatlån"],
  }),
  loanstep: P({
    id: "loanstep", slug: "loanstep", name: "Loanstep", initials: "LN",
    color: "from-cyan-500 to-blue-700", logoUrl: lg_loanstep,
    minAmount: 1000, maxAmount: 50000,
    termLabel: "54 - 76 mån", term: "54 – 76 månader",
    rate: "22,00% fast", setupFee: "589 kr", adminFee: "69 kr", invoiceFee: "0 kr",
    usesUc: false, directPayout: true, allowsRemarks: true,
    minAge: 21,
    rating: 4.4, reviews: 3200,
    categories: ["Snabblån", "SMS-lån", "Lån utan UC"],
    badge: "FAST RÄNTA",
    example: "Ett lån på 25 589 kronor taget 2026-08-01 till 22,00 procents rörlig ränta med en återbetalningstid på 70 månader medför 70 avbetalningar om ca 723 kr, 589 kronor i förmedlingsavgift samt 69 kronor i månatlig administrationsavgift. Detta ger en effektiv ränta på totalt 30,98 procent och totalt att återbetala blir 51 178,00 kronor. Återbetalningstid 54–76 månader.",
    description: "Snabblån utan UC (för belopp under 25 000 kr). Fast ränta hela löptiden.",
    tags: ["Utan UC", "Fast ränta"],
  }),
  finlo: P({
    id: "finlo", slug: "finlo", name: "Finlo", initials: "FI",
    color: "from-neutral-700 to-neutral-900", logoUrl: lg_finlo,
    minAmount: 5000, maxAmount: 800000,
    termLabel: "1 - 20 år", term: "1 – 20 år",
    rate: "4,95% - 23,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: true, allowsRemarks: true,
    minAge: 18, minIncome: 10000, requiresFixedEmployment: true,
    rating: 4.5, reviews: 1282,
    categories: ["Privatlån", "Samla lån", "Billån", "Renovering", "Resa", "Övrigt"],
    example: "Annuitetslån 12 år, belopp 400 000 kr, rörlig ränta 7,99%, uppläggningskostnad 400 kr, aviavgift 20 kr, ger en effektiv ränta på 8,41%. Totalt belopp att återbetala 626 457 kr, fördelat på 144 återbetalningar, ger en månadskostnad på 4 348 kr. Återbetalningstid 1-20 år. Maximala räntan är 23,00%. Räntespann mellan: 4,95% - 23,00%. Uppdaterat 2025-03-01",
    description: "Låneförmedlare som jämför flera banker i en och samma ansökan – anmärkningar kan accepteras.",
    tags: ["Jämför banker", "Anmärkning OK"],
  }),
  ekonomen: P({
    id: "ekonomen", slug: "ekonomen", name: "Ekonomen", initials: "EK",
    color: "from-slate-600 to-slate-800", logoUrl: lg_ekonomen,
    minAmount: 5000, maxAmount: 800000,
    termLabel: "1 - 20 år", term: "1 – 20 år",
    rate: "4,95% - 23,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 18, minIncome: 8000, requiresFixedEmployment: true,
    rating: 4.7, reviews: 8700,
    categories: ["Privatlån", "Samla lån", "Billån", "Renovering", "Övrigt"],
    example: "Annuitetslån 12 år, belopp 400 000 kr, rörlig ränta 7,99%, uppläggningskostnad 400 kr, aviavgift 20 kr, ger en effektiv ränta på 8,41%. Totalt belopp att återbetala 626 457 kr, fördelat på 144 återbetalningar, ger en månadskostnad på 4 348 kr. Återbetalningstid 1-20 år. Maximala räntan är 23,00%. Räntespann mellan: 4,95% - 23,00%. Uppdaterat 2025-03-01",
    description: "Kostnadsfri lånejämförelse med personliga bud från flera banker – en ansökan, en kreditupplysning.",
    tags: ["Kostnadsfritt", "1 UC"],
  }),
  enkelfinans: P({
    id: "enkelfinans", slug: "enkelfinans", name: "Enkelfinans", initials: "EF",
    color: "from-rose-500 to-red-700", logoUrl: lg_enkelfinans,
    minAmount: 5000, maxAmount: 800000,
    termLabel: "1 - 20 år", term: "1 – 20 år",
    rate: "4,95% - 23,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 18, minIncome: 8000, requiresFixedEmployment: true,
    rating: 4.7, reviews: 8700,
    categories: ["Privatlån", "Samla lån", "Billån", "Renovering", "Resa", "Övrigt"],
    example: "Annuitetslån 12 år, belopp 400 000 kr, rörlig ränta 7,99%, uppläggningskostnad 400 kr, aviavgift 20 kr, ger en effektiv ränta på 8,41%. Totalt belopp att återbetala 626 457 kr, fördelat på 144 återbetalningar, ger en månadskostnad på 4 348 kr. Återbetalningstid 1-20 år. Maximala räntan är 23,00%. Räntespann mellan: 4,95% - 23,00%. Uppdaterat 2025-03-01",
    description: "Kostnadsfri lånejämförelse där en ansökan ger personliga bud från flera banker – anmärkningar kan accepteras.",
    tags: ["Kostnadsfritt", "Anmärkning OK"],
  }),
  moneezy: P({
    id: "moneezy", slug: "moneezy", name: "Moneezy", initials: "MO",
    color: "from-sky-500 to-blue-700", logoUrl: lg_moneezy,
    minAmount: 1000, maxAmount: 800000,
    termLabel: "1 - 20 år", term: "1 – 20 år",
    rate: "4,95% - 23,00%", setupFee: "0 kr", adminFee: "0 kr", invoiceFee: "0 kr",
    usesUc: true, directPayout: false, allowsRemarks: true,
    minAge: 18, minIncome: 10000, requiresFixedEmployment: true,
    rating: 4.3, reviews: 1282,
    categories: ["Privatlån", "Samla lån", "Snabblån", "Billån", "Övrigt"],
    example: "Vid ett lån på 310 000 kr till 8,14 % bunden årsränta (eff. ränta på 8,5 %) upplagt på 12 år. Totalt att återbetala 487 872 kr inkl. avgifter. (3 388 kr/mån.). Max. eff. ränta: 22.99 %.",
    description: "Internationell jämförelsetjänst för lån – matchar din ansökan mot flera svenska kreditgivare från 1 000 kr.",
    tags: ["Från 1 000 kr", "Jämför banker"],
  }),
};

/** Tillfälligt dolda långivare – tas bort från hela sajten tills vidare. */
export const HIDDEN_LENDER_IDS: string[] = ["anyfin", "plus1stora", "plus1lilla"];

export const LENDERS: Record<string, Lender> = Object.fromEntries(
  Object.entries(ALL_LENDERS).filter(([id]) => !HIDDEN_LENDER_IDS.includes(id)),
);

export const LENDER_LIST: Lender[] = Object.values(LENDERS);

/** Vilket kreditupplysningsbolag långivaren använder istället för UC. */
export const UC_SOURCE: Record<string, string> = {
  klaralan: "Creditsafe / Bisnode",
  fairlo: "D&B / Bisnode",
  northmill: "Creditsafe",
  brixokk: "D&B / Bisnode",
  brixopl: "D&B / Bisnode",
  credifi: "Creditsafe",
  saldo: "D&B",
  creditstar: "D&B / Bisnode",
  tomly: "Creditsafe",
  ferratum: "Creditsafe",
  lumify: "Creditsafe",
  flexkonto: "D&B / Bisnode",
  merax: "D&B / Bisnode",
  banky: "Creditsafe",
  loanstep: "Creditsafe",
};

export function ucLabel(l: Lender): string {
  if (l.usesUc) return "Nej";
  const src = UC_SOURCE[l.id];
  return src ? `Ja (${src})` : "Ja";
}

export const APPLY_URLS: Record<string, string> = {
  lendo: "https://go.adt228.com/t/t?a=1060519344&as=2098779804&t=2&tk=1",
  enklare: "https://do.enklare.se/t/t?a=1244647179&as=2098779804&t=2&tk=1",
  sambla: "https://go.adt284.net/t/t?a=1923302872&as=2098779804&t=2&tk=1",
  advisa: "https://go.adt284.net/t/t?a=1923306192&as=2098779804&t=2&tk=1",
  axo: "https://go.adt267.com/t/t?a=1948981390&as=2098779804&t=2&tk=1",
  loansse: "https://go.adt267.com/t/t?a=1810104150&as=2098779804&t=2&tk=1",
  klaralan: "https://go.adt267.com/t/t?a=1153212131&as=2098779804&t=2&tk=1",
  fairlo: "https://go.adt242.com/t/t?a=1433384226&as=2098779804&t=2&tk=1",
  northmill: "https://go.adt267.com/t/t?a=1105916291&as=2098779804&t=2&tk=1",
  brixokk: "https://go.adt284.net/t/t?a=1091548601&as=2098779804&t=2&tk=1",
  brixopl: "https://go.adt291.com/t/t?a=1852499982&as=2098779804&t=2&tk=1",
  credifi: "https://go.adt228.com/t/t?a=1673075264&as=2098779804&t=2&tk=1",
  saldo: "https://go.adt256.com/t/t?a=1877254678&as=2098779804&t=2&tk=1",
  creditstar: "https://go.creditstar.se/t/t?a=1896267775&as=2098779804&t=2&tk=1",
  tomly: "https://go.adt256.com/t/t?a=1934379288&as=2098779804&t=2&tk=1",
  ferratum: "https://go.adt212.net/t/t?a=679911485&as=2098779804&t=2&tk=1",
  lumify: "https://go.adt246.net/t/t?a=1278835897&as=2098779804&t=2&tk=1",
  flexkonto: "https://go.adt291.com/t/t?a=1177156373&as=2098779804&t=2&tk=1",
  merax: "https://go.adt291.com/t/t?a=1184128142&as=2098779804&t=2&tk=1",
  plus1stora: "https://www.plus1.com/",
  plus1lilla: "https://www.plus1.com/",
  northmillsamla: "https://go.adt246.net/t/t?a=1963383809&as=2098779804&t=2&tk=1",
  anyfin: "https://anyfin.com/sv_SE",
  banky: "https://go.adt267.com/t/t?a=1745445620&as=2098779804&t=2&tk=1",
  thorn: "https://go.adt228.com/t/t?a=35567344&as=2098779804&t=2&tk=1",
  loanstep: "https://go.adt284.net/t/t?a=1961010341&as=2098779804&t=2&tk=1",
  finlo: "https://go.adt231.net/t/t?a=1897775047&as=2098779804&t=2&tk=1",
  ekonomen: "https://go.adt267.com/t/t?a=1715408972&as=2098779804&t=2&tk=1",
  enkelfinans: "https://go.adt228.com/t/t?a=1587476854&as=2098779804&t=2&tk=1",
  moneezy: "https://go.adt228.com/t/t?a=2083789504&as=2098779804&t=2&tk=1",
};

export function applyUrl(l: Lender, epi?: string, epi2?: string): string {
  const url = APPLY_URLS[l.id] ?? "#";
  if (!url || (!epi && !epi2)) return url;
  try {
    const host = new URL(url).hostname;
    const isAdtraction = /^(go|do|track|cdn)\.adt\d+\.(com|net|se)$/i.test(host)
      || /^(track\.adtraction\.com|do\.enklare\.se|go\.creditstar\.se)$/i.test(host);
    if (!isAdtraction) return url;
  } catch {
    return url;
  }
  let result = url;
  const add = (key: string, value: string) => {
    result += (result.includes("?") ? "&" : "?") + `${key}=${encodeURIComponent(value)}`;
  };
  if (epi) add("epi", epi);
  if (epi2) add("epi2", epi2);
  return result;
}
